import Vue from 'vue'

import permissionMixin        from './mixins/permissionMixin' 

// HOWITWORKS - These mixins are only used on this file, but are created separately for 2 reasons: 1) The functions inside them are used for specific purposes and 2) Since the scope is specific to the need, it reduces the clutter here so this file is more focused on the global and reusable necessities of this Vue component

import initFiltersMixin       from './mixins/AppData/initFiltersMixin'
import handleFiltersAndSorts  from './mixins/AppData/handleFiltersAndSorts'
import handlePushToken        from './mixins/AppData/handlePushToken'

/* 
  HOWITWORKS

  This function will return an AppData.vue instance. It takes an appDefault (the app.js template), the store, and the router. More comments as we go along.
*/

function initAppData (params) {

  return new Vue({

    mixins: [permissionMixin, initFiltersMixin, handleFiltersAndSorts, handlePushToken],

    data () {
      return {
        
        app: _.cloneDeep(params.appDefault), // Now our app.js template is a property of a Vue instance, meaning we can user watchers, computed properties, and more. This will become powerful later in this component.
      }
    },
    router: params.router, // This makes the Router globally accessible
    store: params.store, // This makes the Store globally acessible
    methods: {

      // Completely resets this.app to params.appDefaults, used when signing out.
      resetAppData () {
        this.app = _.cloneDeep(params.appDefault);
      },
      
      // Load everything necessary from the first connect. This will be either called from the user login page, or App.vue's "beforeCreate" hook finding a token in local storage and using it to log the user in.

      firstLoad () {
        
        // Turn the TOS Check into a Promisified function so we can make it part of the load sequence and lock out users who don't accept our updated TOS
        var tosCheck = function () {
          return new Promise((resolve, reject) => {
            
            if (this.$store.getters.appVersion.tosVersion > this.$store.getters.profile.tosLastRead) {
              window.Dialog.showDialog({
                title: 'Policy Update',
                confirm: {
                  title: 'Continue',
                  action () {
                    return window.Dialog.$refs.componentRef.action()
                    .then(data => {
                      return resolve();
                    });
                  }
                },
                component: 'vo-privacy-lockout',
                modal: true
              });
            }
            else {
              return resolve();
            }
            
          });
        }.bind(this);
        
        // Turn the version check into a promisified function for the same reason
        var versionCheck = function () {

          return new Promise((resolve, reject) => {

            // Check Version
            var higherVersion = this.getHigherVersion(VERSION, this.$store.getters.appVersion.current);
            var updateRequired = this.getHigherVersion(VERSION, this.$store.getters.appVersion.minimum) === this.$store.getters.appVersion.minimum && (this.$store.getters.appVersion.minimum !== VERSION);
                      
            // User is using a version below the required version
            if (updateRequired) {
              window.Toast.show('Update required (v' + this.$store.getters.appVersion.current + ')', 'notify', {duration: 10000});
              this.app.versionLockout = true;
            }
            // User is using a version that is lower than the newest version
            else if (higherVersion === this.$store.getters.appVersion.current && this.$store.getters.appVersion.current !== VERSION) {
              window.Toast.show('Update v' + this.$store.getters.appVersion.current + ' available.', 'notify', {duration: 10000});
              return resolve();
            }

            // User is using at least the highest version according to the server
            else if (higherVersion === VERSION) {

              // First time using this version
              if (VERSION !== this.$store.getters.profile.lastVersionUsed) {
                
                window.Toast.show('Vilio has been updated to v' + VERSION, 'success');
                
                if (this.$store.getters.profile.promptChangelog) {
                  this.$router.push({
                    query: {
                      dialog: 'changelog'
                    }
                  });
                }
                
                this.$store.dispatch('saveProfile', {
                  user: {
                    lastVersionUsed: VERSION
                  }
                })
                .then(data => {
                  return resolve();
                })
                .catch(e => {
                  console.log(e);
                })
              }
              else {
                return resolve();
              }
            }

          });
        }.bind(this);

        // If the user doesn't have any roles, log them out for now. // IMPROVE: The user should probably still be able to change their settings, or have an option to delete their account.
        if (this.$store.getters.profile.roles.length === 0) {
          
          Dialog.showDialog({
            title: 'No Roles',
            body: 'Your Vilio account is not associated with any units or properties. For more information, please contact your landlord or property manager.',
            dismiss: {
              title: 'Dismiss'
            }
          });

          return AppData.$store.dispatch('signout')
          .then(data => {
            this.$router.replace({ name: 'login' });
            return Promise.reject({
              code: ERRORS.NO_ROLES
            });
          });
        
        }
        else {
        
          // Perform the TOS check and the Version check before allowing the user to load the rest of the data for the applicaiton.
          return tosCheck()
          .then(data => {
            return versionCheck();
          })
          .then(data => {
            return this.prepareApp();
          })
          .catch(e => {
            console.log('Error during startup checks:', e);
          });
        }
        
      },

      // Prepare all the data the app needs, such as initializing list instances, and loading all required content from the server 
      prepareApp () {

        // Reset App
        this.resetAppData();
        
        // Initialize Page Lists
        for (var page in this.app.pages) {
          
          if (this.app.pages[page].listParams) {
            Vue.set(this.app.pages[page], 'list', new ListModel(this.app.pages[page].listParams));
          }
        }
        
        // Init Activity List
        Vue.set(this.app.activity, 'list', new ListModel({...params.appDefault.activity.listParams}));
        
        return Promise.all([
          this.$store.dispatch('fetchCompany', this.$store.getters.profile.roles[0].company)
        ])
        .then(data => {
        
          // Load "All" Role Templates, Properties, and Ticket Tags
          // IMPROVE: This should be revised as the app will break if they have more than the server will allow us to fetch at at time

          var promises = [
            this.$store.dispatch('loadRoleTemplates', {
              company: this.$store.getters.company._id
            }),
            this.$store.dispatch('loadProperties', {
              company: this.$store.getters.company._id
            }),
            this.$store.dispatch('loadTicketTags', {
              company: this.$store.getters.company._id
            })
          ];

          return Promise.all(promises)
          .then(data => {

            // Check if one of the Company tabs is selected, preset it properly
            if (this.$route.name === 'roleTemplates' || this.$route.name === 'ticketTags') {
              Vue.set(this.app.navigation.company, 'target', this.$route.name);
            }

            // Initialize Filters
            this.initFilters();

            // Init Push Notifications
            if (window.cordova) {
              this.initPush({ profileLoaded: true });
            }

            console.log('AppData.firstLoad - Completed');

            Vue.set(this.app, 'ready', true); // Tells App.vue everything is loaded properly
            return Promise.resolve();
          })
        })
        .catch(e => {
          console.log('AppData.firstLoad Error:', e);
          return Promise.reject(e);
        });
      },

      // This will return the higher version between the two passed in
      getHigherVersion (v1, v2) {
        if (v1 === v2) return v1; //same

        var v1s = v1.split('.');
        var v2s = v2.split('.');
      
        for (var i = 0; i < v1s.length; ++i) {
          if (Number(v1s[i]) < Number(v2s[i]))       return v2; // v2 is bigger
          else if (Number(v1s[i]) > Number(v2s[i]))  return v1; // v1 is bigger
        }
        return v1; // same
      },

      // NAV OPTIONS

      // The feedback link is different for Managers and Residents, so to open the correct page, we need the function to exist somewhere we have access to checking their permissions (like here!).
      openFeedback () {
        window.open(this.hasPermissionsInProperty(['ticketManager', 'ticketAdmin']) ? window.urls.feedback.manager : window.urls.feedback.resident);
      },

      clearLocalStorage () {

        var clearItems = function () {
          // We don't want to prompt the user to download the app every time they signout, so we'll preserve hideAppPrompt in storage
          var hideAppPrompt = !!localStorage.getItem('hideAppPrompt'); 
          localStorage.clear();
          if (hideAppPrompt) localStorage.setItem('hideAppPrompt', hideAppPrompt); 
        };

        var pushToken = localStorage.getItem('pushToken') || false;

        if (pushToken) {

          this.unregisterPush(pushToken)
          .then(data => {
            clearItems();
          })
          .catch(e => {
            console.log('Error clearing push token: ', e);
          })
        }
        else {
          clearItems();
        }
      }
    }
  });
}

export default initAppData;


// WEBPACK FOOTER //
// ./src/appDataCmp.js